import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  forwardRef,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { concatLatestFrom } from '@ngrx/operators';
import { BehaviorSubject, Observable, map, startWith } from 'rxjs';

import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { AutoCompleteOption } from '../stena-auto-complete/auto-complete-option';
import { ControlValueAccessorConnector } from '../utils/control-value-accessor-connector/control-value-accessor-connector';
@Component({
  selector: 'stena-chips-list',
  templateUrl: './stena-chips-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StenaChipsListComponent),
      multi: true,
    },
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatChipsModule,
    DragDropModule,
    MatAutocompleteModule,
  ],
})
export class StenaChipsListComponent
  extends ControlValueAccessorConnector<string[]>
  implements OnInit
{
  @Input() label?: string;
  @Input() placeholder = 'Search';
  @Input() isWithAutoComplete = true;
  inputField = '';

  separatorKeysCodes: number[] = [ENTER, COMMA];
  @Input() set autoCompleteOptions(options: AutoCompleteOption[]) {
    this.options$.next(options);
  }
  filteredOptions$!: Observable<AutoCompleteOption[]>;
  options$ = new BehaviorSubject<AutoCompleteOption[]>([]);
  autoCompleteFormControl: FormControl<string | null> = new FormControl();
  @ViewChild('autoSelectInput') autoSelectInput:
    | ElementRef<HTMLInputElement>
    | undefined;

  ngOnInit(): void {
    if (this.isWithAutoComplete) {
      this.autoCompleteFormControl.addValidators(
        (control: AbstractControl): ValidationErrors | null => {
          const valid =
            (this.options$.value || []).findIndex(
              (a) => a.value === control.value
            ) > -1;

          return valid
            ? null
            : { autoCompleteOptionInvalid: { value: control.value } };
        }
      );
      this.filteredOptions$ = this.autoCompleteFormControl.valueChanges.pipe(
        startWith(''),
        concatLatestFrom(() => this.options$),
        map(([searchVal, options]) => {
          return !searchVal
            ? options
            : options.filter((op) =>
                op.displayName.toLowerCase().includes(searchVal)
              );
        })
      );
    }
  }

  onAddChipFromInput(item: MatChipInputEvent): void {
    this.control.setValue([...(this.control.value || []), item.value]);
    this.inputField = '';
  }

  onRemoveItem(item: string): void {
    this.control.setValue(
      (this.control.value || []).filter((chip) => chip !== item)
    );
  }

  onChipDropped(chip: CdkDragDrop<string>): void {
    moveItemInArray(
      this.control.value || [],
      chip.previousIndex,
      chip.currentIndex
    );
  }

  trackChipBy(index: number, item: string): string {
    return item;
  }

  onAddChipFromSelectedValue(item: MatChipInputEvent): void {
    const value = item.value;
    const isValidOption =
      this.options$.value.findIndex((option) => option.value === +value) > -1;
    if (!this.control.value?.includes(value) && isValidOption) {
      this.control.setValue([...(this.control.value || []), value]);
    }
    this.inputField = '';
  }

  displayFn(value: number): string {
    return !value
      ? ''
      : (this.options$.value.find((a) => a.value === value) || {})
          ?.displayName || '';
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const value = event.option.value;

    if (!this.control.value?.includes(value)) {
      this.control.setValue([...(this.control.value || []), value]);
    }
    if (this.autoSelectInput) {
      this.autoSelectInput.nativeElement.value = '';
    }
    this.autoCompleteFormControl.setValue(null);
  }

  trackOptionBy(index: number, _option: AutoCompleteOption): number {
    return index;
  }
}
